<template>
  <div class="avant-apres" :style="overlayBgStyle">
    <div class="closeButtons">
      <q-btn icon="close" flat round dense aria-label="Fermer" @click="$emit('closeAvantApres')" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <div v-if="loading" class="state-panel">Chargement...</div>
    <div v-else-if="pairs.length === 0" class="state-panel">Aucun objet à comparer</div>

    <div v-else class="stage">
      <q-btn
        round
        class="nav-button previous-button"
        size="lg"
        icon="arrow_back_ios_new"
        aria-label="Paire précédente"
        @click="previous"
      />

      <div class="pair-frame">
        <!-- Paire complète (2 images) : comparateur -->
        <div
          v-if="!currentPair.single && currentPair.before.type === 'image' && currentPair.after.type === 'image'"
          ref="comparatorRef"
          class="comparator"
          :class="{ dragging }"
          role="slider"
          tabindex="0"
          :aria-valuenow="Math.round(sliderPos)"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label="Comparateur avant après, glisser pour révéler"
          @pointerdown="onPointerDown"
          @pointermove="onPointerMove"
          @pointerup="onPointerUp"
          @pointercancel="onPointerUp"
        >
          <img class="cmp-img cmp-after" :src="currentPair.after.url" :alt="currentPair.after.title || 'Après'" draggable="false" />
          <img
            class="cmp-img cmp-before"
            :src="currentPair.before.url"
            :alt="currentPair.before.title || 'Avant'"
            draggable="false"
            :style="{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }"
          />

          <span class="pill pill-before">Avant</span>
          <span class="pill pill-after">Après</span>

          <div class="handle" :style="{ left: `${sliderPos}%` }" aria-hidden="true">
            <div class="handle-line"></div>
            <div class="handle-knob">
              <q-icon name="chevron_left" />
              <q-icon name="chevron_right" />
            </div>
          </div>
        </div>

        <!-- Diapo simple (dernier objet impair) -->
        <div v-else-if="currentPair.single" class="single-slide">
          <img
            v-if="currentPair.before.type === 'image'"
            :src="currentPair.before.url"
            :alt="currentPair.before.title || ''"
            draggable="false"
          />
          <div v-else class="file-slot">
            <q-icon :name="fileIcon(currentPair.before.type)" />
            <span>{{ currentPair.before.title || 'Objet' }}</span>
          </div>
        </div>

        <!-- Cas dégradé : au moins un objet non-image → côte à côte -->
        <div v-else class="fallback-pair">
          <div class="fallback-slot">
            <span class="pill pill-before pill-static">Avant</span>
            <img v-if="currentPair.before.type === 'image'" :src="currentPair.before.url" :alt="currentPair.before.title || ''" draggable="false" />
            <div v-else class="file-slot">
              <q-icon :name="fileIcon(currentPair.before.type)" />
              <span>{{ currentPair.before.title || 'Objet' }}</span>
            </div>
          </div>
          <div class="fallback-slot">
            <span class="pill pill-after pill-static">Après</span>
            <img v-if="currentPair.after.type === 'image'" :src="currentPair.after.url" :alt="currentPair.after.title || ''" draggable="false" />
            <div v-else class="file-slot">
              <q-icon :name="fileIcon(currentPair.after.type)" />
              <span>{{ currentPair.after.title || 'Objet' }}</span>
            </div>
          </div>
        </div>
      </div>

      <q-btn
        round
        class="nav-button next-button"
        size="lg"
        icon="arrow_forward_ios_new"
        aria-label="Paire suivante"
        @click="next"
      />

      <div v-if="pairs.length > 1" class="dots" aria-label="Navigation des paires">
        <button
          v-for="(_, index) in pairs"
          :key="index"
          type="button"
          :class="{ active: index === currentIndex }"
          :aria-label="`Paire ${index + 1}`"
          @click="goTo(index)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

const getResourceType = (name) => {
  const n = name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|avif)$/i.test(n)) return 'image';
  if (/\.(mp4|mov|avi|webm|mkv)$/i.test(n)) return 'video';
  if (/\.(mp3|wav|ogg|m4a|flac)$/i.test(n)) return 'audio';
  if (/\.(pdf)$/i.test(n)) return 'pdf';
  if (/\.(docx?|txt|rtf|odt)$/i.test(n)) return 'doc';
  return 'file';
};

export default {
  name: 'AvantApres',
  components: { ReturnHomeButton },
  props: ['blockDatas'],
  emits: ['closeAvantApres', 'closeAllDialogs'],
  setup(props) {
    const { overlayBgStyle } = useOverlayBg();

    const items = ref([]);
    const loading = ref(true);
    const currentIndex = ref(0);
    const sliderPos = ref(50);
    const dragging = ref(false);
    const comparatorRef = ref(null);

    // Construit les paires par ordre : [0,1], [2,3]... dernier seul si impair.
    const pairs = computed(() => {
      const list = items.value;
      const result = [];
      for (let i = 0; i < list.length; i += 2) {
        if (i + 1 < list.length) {
          result.push({ before: list[i], after: list[i + 1], single: false });
        } else {
          result.push({ before: list[i], after: list[i], single: true });
        }
      }
      return result;
    });

    const currentPair = computed(() => pairs.value[currentIndex.value] || null);

    const fetchResources = async () => {
      loading.value = true;
      const associated = (props.blockDatas && props.blockDatas.associatedResources) || [];
      const temp = [];
      for (let i = 0; i < associated.length; i++) {
        const resource = associated[i];
        try {
          const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
          temp.push({
            url: response.data,
            title: resource.title || '',
            type: getResourceType(resource.name),
            index: resource.index !== undefined && resource.index !== null ? Number(resource.index) : i,
            position: i,
          });
        } catch (error) {
          console.error('Erreur lors de la récupération de la ressource :', error);
        }
      }
      temp.sort((a, b) => {
        if (a.index === b.index) return a.position - b.position;
        return a.index - b.index;
      });
      items.value = temp;
      loading.value = false;
    };

    const goTo = (index) => {
      if (!pairs.value.length) return;
      currentIndex.value = (index + pairs.value.length) % pairs.value.length;
      sliderPos.value = 50;
    };
    const next = () => goTo(currentIndex.value + 1);
    const previous = () => goTo(currentIndex.value - 1);

    const updateFromClientX = (clientX) => {
      const el = comparatorRef.value;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (!rect.width) return;
      let pct = ((clientX - rect.left) / rect.width) * 100;
      if (pct < 0) pct = 0;
      if (pct > 100) pct = 100;
      sliderPos.value = pct;
    };

    const onPointerDown = (event) => {
      dragging.value = true;
      if (event.target.setPointerCapture) {
        try { event.target.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
      }
      updateFromClientX(event.clientX);
    };
    const onPointerMove = (event) => {
      if (!dragging.value) return;
      updateFromClientX(event.clientX);
    };
    const onPointerUp = () => {
      dragging.value = false;
    };

    const onKeyDown = (event) => {
      if (event.key === 'ArrowRight') {
        next();
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        previous();
        event.preventDefault();
      }
    };

    const fileIcon = (type) => {
      if (type === 'audio') return 'audiotrack';
      if (type === 'video') return 'movie';
      if (type === 'pdf' || type === 'doc') return 'description';
      return 'insert_drive_file';
    };

    // Sécurité : si l'index dépasse après un changement de données.
    watch(pairs, (list) => {
      if (currentIndex.value > list.length - 1) currentIndex.value = 0;
    });

    onMounted(() => {
      fetchResources();
      window.addEventListener('keydown', onKeyDown);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onKeyDown);
    });

    return {
      overlayBgStyle,
      loading,
      pairs,
      currentPair,
      currentIndex,
      sliderPos,
      dragging,
      comparatorRef,
      goTo,
      next,
      previous,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      fileIcon,
    };
  },
};
</script>

<style scoped>
.avant-apres {
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: rgba(217, 217, 217, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: aa-fade-in 200ms ease;
}

.closeButtons {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.state-panel {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  color: #333;
  font-size: 1.2rem;
}

.stage {
  display: grid;
  grid-template-columns: minmax(48px, 72px) minmax(0, 1fr) minmax(48px, 72px);
  align-items: center;
  gap: clamp(8px, 2vw, 24px);
  width: 100%;
  height: 100%;
  padding: 76px clamp(12px, 3vw, 48px) 64px;
}

.nav-button {
  justify-self: center;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  color: #1f1f1f;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
}

.pair-frame {
  justify-self: center;
  width: min(80vw, 1000px);
  height: 70vh;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* --- Comparateur --- */
.comparator {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 12px 38px rgba(0, 0, 0, 0.28);
  touch-action: none;
  cursor: ew-resize;
  user-select: none;
}

.cmp-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.cmp-before {
  z-index: 2;
}

.pill {
  position: absolute;
  top: 14px;
  z-index: 4;
  padding: 4px 12px;
  font-size: 0.85rem;
  color: #fff;
  background: rgba(0, 0, 0, 0.55);
  letter-spacing: 0.03em;
  transition: opacity 160ms ease;
}

.pill-before {
  left: 14px;
}

.pill-after {
  right: 14px;
}

.comparator.dragging .pill {
  opacity: 0;
}

.handle {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 5;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.handle-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #fff;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
}

.handle-knob {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #fff;
  color: #1f1f1f;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
}

.handle-knob .q-icon {
  font-size: 20px;
  margin: 0 -4px;
}

/* --- Diapo simple / fallback --- */
.single-slide,
.file-slot {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.single-slide img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.file-slot {
  flex-direction: column;
  gap: 14px;
  color: #333;
  text-align: center;
  padding: 24px;
}

.file-slot .q-icon {
  font-size: 72px;
}

.fallback-pair {
  width: 100%;
  height: 100%;
  display: flex;
  gap: clamp(8px, 2vw, 20px);
}

.fallback-slot {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
}

.fallback-slot img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.pill-static {
  opacity: 1;
}

/* --- Dots --- */
.dots {
  grid-column: 1 / -1;
  justify-self: center;
  display: flex;
  gap: 14px;
  margin-top: 8px;
}

.dots button {
  width: 14px;
  height: 14px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: transform 160ms ease, background 160ms ease;
}

.dots button.active {
  background: #1f1f1f;
  transform: scale(1.2);
}

@keyframes aa-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (max-width: 900px) {
  .stage {
    grid-template-columns: 48px minmax(0, 1fr) 48px;
    padding: 70px 10px 56px;
  }

  .pair-frame {
    width: calc(100vw - 120px);
    height: 62vh;
  }
}

@media (max-width: 640px) {
  .stage {
    grid-template-columns: 42px minmax(0, 1fr) 42px;
    gap: 6px;
  }

  .pair-frame {
    width: calc(100vw - 96px);
    height: 58vh;
  }

  .handle-knob {
    width: 40px;
    height: 40px;
  }

  .pill {
    font-size: 0.75rem;
    padding: 3px 9px;
  }
}
</style>
